import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { useActiveCompany } from '../hooks/useActiveCompany';
import { EmployeeSelect } from '../components/EmployeeAutocomplete';
import {
  FileText, Download, Send, CheckCircle2, RefreshCw, Sparkles,
  Layers, Clock, ShieldCheck, FileCheck, ChevronRight, Edit3,
  AlertTriangle, RotateCcw, Eye, EyeOff, TrendingUp, Loader2,
  IndianRupee, Save, Database, X, Check, Info, Lock,
  Users, UserCheck, UserX, Filter, Mail, CheckSquare, Square,
  Trash2, HelpCircle, AlertCircle, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { downloadDocumentPdf } from '../utils/downloadPdf';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Doc types that support the Salary Breakup Editor
const SALARY_EDITOR_DOC_TYPES = new Set(['appointment_order', 'offer_letter']);

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────
const INR = (v) =>
  '₹ ' + (Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function computeLiveTotals(sections) {
  let grossM = 0, dedM = 0, empM = 0, perkM = 0;
  if (!sections) return {};

  for (const sec of sections) {
    const cat = (sec.category_type || '').toLowerCase();
    let secSum = 0;
    for (const c of (sec.components || [])) {
      if (c._enabled === false) continue;
      const m = Number(c.monthly_amount) || 0;
      secSum += m;
    }
    if (['earning', 'earnings', 'gross'].includes(cat)) grossM += secSum;
    else if (['deduction', 'deductions'].includes(cat)) dedM += secSum;
    else if (cat.includes('employer')) empM += secSum;
    else if (cat.includes('perk')) perkM += secSum;
    else grossM += secSum;
  }

  const grossA = +(grossM * 12).toFixed(2);
  const dedA = +(dedM * 12).toFixed(2);
  const empA = +(empM * 12).toFixed(2);
  const perkA = +(perkM * 12).toFixed(2);
  const takeHomeM = +Math.max(0, grossM - dedM).toFixed(2);
  const takeHomeA = +(takeHomeM * 12).toFixed(2);
  const ctcM = +(grossM + empM + perkM).toFixed(2);
  const ctcA = +(ctcM * 12).toFixed(2);

  return {
    gross_monthly: +grossM.toFixed(2),
    gross_annual: grossA,
    total_deductions_monthly: +dedM.toFixed(2),
    total_deductions_annual: dedA,
    total_employer_monthly: +empM.toFixed(2),
    total_employer_annual: empA,
    total_perks_monthly: +perkM.toFixed(2),
    total_perks_annual: perkA,
    take_home_monthly: takeHomeM,
    take_home_annual: takeHomeA,
    ctc_monthly: ctcM,
    ctc_annual: ctcA,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Salary Component Row
// ────────────────────────────────────────────────────────────────────────────
function SalaryRow({ comp, onChange, readOnly }) {
  const handleMonthly = (e) => {
    const m = parseFloat(e.target.value) || 0;
    onChange({ ...comp, monthly_amount: m, annual_amount: +(m * 12).toFixed(2) });
  };
  const handleAnnual = (e) => {
    const a = parseFloat(e.target.value) || 0;
    onChange({ ...comp, annual_amount: a, monthly_amount: +(a / 12).toFixed(2) });
  };

  return (
    <tr className={`border-b border-slate-100 transition-colors ${comp._enabled === false ? 'opacity-40 bg-slate-50' : 'hover:bg-slate-50/60'}`}>
      <td className="px-3 py-1.5 w-5">
        {!readOnly && (
          <input
            type="checkbox"
            checked={comp._enabled !== false}
            onChange={(e) => onChange({ ...comp, _enabled: e.target.checked })}
            className="accent-blue-600 w-3.5 h-3.5 cursor-pointer"
          />
        )}
      </td>
      <td className="px-3 py-1.5">
        <span className="font-medium text-slate-800 text-xs">{comp.name || comp.head_name || comp.code}</span>
        <span className="ml-1.5 text-[9px] font-mono text-slate-400 uppercase">{comp.code || comp.head_code}</span>
      </td>
      <td className="px-2 py-1.5 w-36">
        {readOnly ? (
          <span className="font-mono text-xs text-slate-800">{INR(comp.monthly_amount)}</span>
        ) : (
          <input
            type="number"
            value={comp.monthly_amount}
            onChange={handleMonthly}
            disabled={comp._enabled === false}
            className="w-full h-7 px-2 text-xs font-mono bg-white border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 disabled:bg-slate-50"
            min="0"
            step="1"
          />
        )}
      </td>
      <td className="px-2 py-1.5 w-36">
        {readOnly ? (
          <span className="font-mono text-xs text-slate-800">{INR(comp.annual_amount)}</span>
        ) : (
          <input
            type="number"
            value={comp.annual_amount}
            onChange={handleAnnual}
            disabled={comp._enabled === false}
            className="w-full h-7 px-2 text-xs font-mono bg-white border border-slate-200 rounded-lg outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 disabled:bg-slate-50"
            min="0"
            step="1"
          />
        )}
      </td>
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Dynamic Salary Section Block
// ────────────────────────────────────────────────────────────────────────────
function SalarySection({ section, secIndex, onChangeComp, readOnly }) {
  const [expanded, setExpanded] = useState(true);
  const components = section.components || [];

  const subtotal = components.reduce(
    (s, c) => s + (c._enabled !== false ? Number(c.monthly_amount) || 0 : 0), 0
  );

  const cat = (section.category_type || '').toLowerCase();
  const colorBg =
    cat.includes('earn') ? 'bg-emerald-50 text-emerald-900 border-emerald-200' :
    cat.includes('deduct') ? 'bg-rose-50 text-rose-900 border-rose-200' :
    cat.includes('employer') ? 'bg-purple-50 text-purple-900 border-purple-200' :
    cat.includes('perk') ? 'bg-amber-50 text-amber-900 border-amber-200' :
    'bg-slate-100 text-slate-900 border-slate-200';

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden mb-3.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full flex items-center justify-between px-4 py-2.5 text-left font-bold text-xs uppercase tracking-wider ${colorBg}`}
      >
        <span>{section.heading || section.title || `Section ${secIndex + 1}`}</span>
        <div className="flex items-center gap-3">
          <span className="font-mono normal-case font-extrabold">{INR(subtotal)} / mo</span>
          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>
      {expanded && (
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-3 py-1.5 w-5"></th>
              <th className="px-3 py-1.5 text-left font-bold text-slate-500 uppercase text-[10px]">Salary Head</th>
              <th className="px-2 py-1.5 text-left font-bold text-slate-500 uppercase text-[10px] w-36">Monthly (₹)</th>
              <th className="px-2 py-1.5 text-left font-bold text-slate-500 uppercase text-[10px] w-36">Annual (₹)</th>
            </tr>
          </thead>
          <tbody>
            {components.map((c, cIdx) => (
              <SalaryRow
                key={c._key || cIdx}
                comp={c}
                onChange={(updated) => onChangeComp(secIndex, cIdx, updated)}
                readOnly={readOnly}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Dynamic Live Annexure Preview
// ────────────────────────────────────────────────────────────────────────────
function AnnexurePreview({ sections, totals, employeeName }) {
  const SubtotalRow = ({ label, m, a, bold }) => (
    <tr className={`${bold ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-700 font-semibold'}`}>
      <td className="px-3 py-2 text-xs uppercase tracking-wide">{label}</td>
      <td className="px-3 py-2 text-right font-mono font-bold text-xs">{INR(m)}</td>
      <td className="px-3 py-2 text-right font-mono font-bold text-xs">{INR(a)}</td>
    </tr>
  );

  return (
    <div className="border border-blue-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-blue-200" />
          <span className="font-bold text-xs uppercase tracking-wide">Live Salary Annexure Preview</span>
        </div>
        {employeeName && <span className="text-blue-200 font-medium text-xs">{employeeName}</span>}
      </div>
      <table className="w-full text-xs">
        <thead className="bg-slate-100">
          <tr>
            <th className="px-3 py-2 text-left font-bold text-slate-600 uppercase text-[10px]">Salary Head</th>
            <th className="px-3 py-2 text-right font-bold text-slate-600 uppercase text-[10px]">Monthly (₹)</th>
            <th className="px-3 py-2 text-right font-bold text-slate-600 uppercase text-[10px]">Annual (₹)</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((sec, sIdx) => {
            const activeComps = (sec.components || []).filter(c => c._enabled !== false);
            const secSubtotal = activeComps.reduce((acc, c) => acc + (Number(c.monthly_amount) || 0), 0);

            return (
              <React.Fragment key={sec.id || sIdx}>
                <tr className="bg-slate-200/80 font-bold text-slate-800">
                  <td colSpan={3} className="px-3 py-1.5 text-[10px] uppercase tracking-wider">{sec.heading}</td>
                </tr>
                {activeComps.map((c, cIdx) => (
                  <tr key={c._key || cIdx} className="border-b border-slate-100">
                    <td className="px-3 py-1.5 text-slate-700">{c.name || c.head_name || c.code}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{INR(c.monthly_amount)}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{INR(c.annual_amount)}</td>
                  </tr>
                ))}
                {sec.show_subtotal !== false && (
                  <SubtotalRow
                    label={sec.subtotal_label || `Subtotal - ${sec.heading}`}
                    m={secSubtotal}
                    a={secSubtotal * 12}
                  />
                )}
              </React.Fragment>
            );
          })}

          {/* E. Summary Section */}
          <tr className="bg-slate-900 text-amber-400 font-bold">
            <td colSpan={3} className="px-3 py-2 text-xs uppercase tracking-wider">E. Salary Summary</td>
          </tr>
          <SubtotalRow label="Gross Salary" m={totals.gross_monthly} a={totals.gross_annual} />
          <SubtotalRow label="Total Deductions" m={totals.total_deductions_monthly} a={totals.total_deductions_annual} />
          <SubtotalRow label="Employer Cost / Perks" m={(totals.total_employer_monthly || 0) + (totals.total_perks_monthly || 0)} a={(totals.total_employer_annual || 0) + (totals.total_perks_annual || 0)} />
          <SubtotalRow label="Fixed CTC" m={totals.ctc_monthly} a={totals.ctc_annual} bold />
          <SubtotalRow label="Take Home Salary" m={totals.take_home_monthly} a={totals.take_home_annual} />
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// CTC Summary Bar
// ────────────────────────────────────────────────────────────────────────────
function CTCSummaryBar({ totals }) {
  const items = [
    { label: 'Gross / Month', value: totals.gross_monthly, color: 'text-emerald-700' },
    { label: 'Deductions / Month', value: totals.total_deductions_monthly, color: 'text-rose-700' },
    { label: 'Take Home / Month', value: totals.take_home_monthly, color: 'text-blue-700' },
    { label: 'Employer Cost / Month', value: totals.total_employer_monthly, color: 'text-purple-700' },
    { label: 'Fixed CTC / Month', value: totals.ctc_monthly, color: 'text-slate-900' },
    { label: 'Annual CTC', value: totals.ctc_annual, color: 'text-slate-900' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
      {items.map(item => (
        <div key={item.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center shadow-xs">
          <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">{item.label}</div>
          <div className={`font-mono font-extrabold text-sm ${item.color}`}>{INR(item.value)}</div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Snapshot View Modal (for already-issued documents)
// ────────────────────────────────────────────────────────────────────────────
function SalarySnapshotModal({ doc, onClose }) {
  if (!doc) return null;
  const annexure = doc.annexure_snapshot;
  if (!annexure) return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-3">
          <h3 className="font-bold text-slate-900">Salary Snapshot</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-xl font-bold">&times;</button>
        </div>
        <p className="text-sm text-slate-500">No salary snapshot stored for this document.</p>
      </div>
    </div>
  );

  const sections = annexure.sections || [];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-slate-800 to-slate-900 text-white">
          <div>
            <h3 className="font-bold text-base flex items-center gap-2">
              <Lock className="w-4 h-4 text-amber-400" />
              Immutable Salary Snapshot — {doc.employee_name}
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Values frozen at issuance on {doc.issued_at?.slice(0, 10)}
              {doc.salary_was_overridden && <span className="ml-2 text-amber-400 font-bold">★ HR-Edited values used</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-xl font-bold p-1">&times;</button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto space-y-3">
          {doc.salary_was_overridden && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 flex items-center gap-2 text-xs text-amber-900">
              <Info className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Salary was edited before issuance by <b>{doc.issued_by_name || 'HR'}</b>.
                {doc.salary_edit_reason ? ` Reason: ${doc.salary_edit_reason}` : ''}
                {doc.employee_master_updated ? ' Employee Salary Master was also updated.' : ' Employee Salary Master was NOT changed.'}
              </span>
            </div>
          )}
          <table className="w-full text-xs border border-slate-200 rounded-xl overflow-hidden">
            <thead className="bg-slate-100">
              <tr>
                <th className="px-3 py-2 text-left font-bold text-slate-600 uppercase text-[10px]">Head</th>
                <th className="px-3 py-2 text-left font-bold text-slate-500 uppercase text-[10px]">Category</th>
                <th className="px-3 py-2 text-right font-bold text-slate-600 uppercase text-[10px]">Monthly</th>
                <th className="px-3 py-2 text-right font-bold text-slate-600 uppercase text-[10px]">Annual</th>
              </tr>
            </thead>
            <tbody>
              {sections.map((sec, sIdx) => (
                <React.Fragment key={sIdx}>
                  <tr className="bg-slate-100 font-bold text-slate-800">
                    <td colSpan={4} className="px-3 py-1.5 text-[10px] uppercase">{sec.heading}</td>
                  </tr>
                  {(sec.components || []).map((c, cIdx) => (
                    <tr key={cIdx} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-1.5 text-slate-800 font-medium">{c.name || c.head_name || c.code}</td>
                      <td className="px-3 py-1.5 capitalize text-slate-500">{sec.category_type}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{INR(c.monthly)}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{INR(c.annual)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot className="bg-slate-900 text-white">
              <tr>
                <td colSpan={2} className="px-3 py-2 font-bold text-xs uppercase">Fixed CTC</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{INR(annexure.totals?.ctc_monthly)}</td>
                <td className="px-3 py-2 text-right font-mono font-bold">{INR(annexure.totals?.ctc_annual)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────────
// Main Component
// ────────────────────────────────────────────────────────────────────────────
export default function AppointmentOrderGenerator() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [employeeDoc, setEmployeeDoc] = useState(null);
  const [docType, setDocType] = useState('appointment_order');
  const [catalog, setCatalog] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  // Mode state: 'single' vs 'bulk' (auto-active when docType === 'increment_letter')
  const [isBulkMode, setIsBulkMode] = useState(false);

  // Multi-employee selection state
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState([]);
  const [masterEmployees, setMasterEmployees] = useState([]);
  const [loadingMasterEmps, setLoadingMasterEmps] = useState(false);

  // Filters for multi-employee selection
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('all');
  const [desigFilter, setDesigFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');

  // Bulk workflow states
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResultModal, setBulkResultModal] = useState(null);
  const [bulkEmailing, setBulkEmailing] = useState(false);
  const [bulkEmailResultModal, setBulkEmailResultModal] = useState(null);

  // Dynamic Template Sections state for Salary Editor (single mode)
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [originalSections, setOriginalSections] = useState(null);
  const [editedSections, setEditedSections] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showAnnexurePreview, setShowAnnexurePreview] = useState(true);

  // Single Generate action state
  const [generating, setGenerating] = useState(false);
  const [salaryEditReason, setSalaryEditReason] = useState('');
  const [showMasterUpdateConfirm, setShowMasterUpdateConfirm] = useState(false);

  // Issued documents list & Repository multi-select state
  const [issuedDocs, setIssuedDocs] = useState([]);
  const [loadingIssued, setLoadingIssued] = useState(false);
  const [auditLog, setAuditLog] = useState(null);
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [snapshotDoc, setSnapshotDoc] = useState(null);
  const [issuedSelectedDocs, setIssuedSelectedDocs] = useState(new Set());

  const fetchCatalog = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/documents/catalog?company_id=${selectedCompany}`, { withCredentials: true });
      if (data.doc_types && data.doc_types.length > 0) {
        setCatalog(data.doc_types);
      }
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => {
    axios.get(`${API_URL}/companies`, { withCredentials: true })
      .then(({ data }) => {
        setCompanies(data);
        setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
      }).catch(() => {});
  }, [setSelectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchCatalog();
    }
  }, [selectedCompany, fetchCatalog]);

  const fetchIssuedDocs = useCallback(async () => {
    if (!selectedCompany) return;
    setLoadingIssued(true);
    try {
      const { data } = await axios.get(`${API_URL}/documents/issued?company_id=${selectedCompany}`, { withCredentials: true });
      setIssuedDocs(data);
      setIssuedSelectedDocs(new Set());
    } catch (e) { console.error(e); }
    setLoadingIssued(false);
  }, [selectedCompany]);

  const fetchMasterEmployees = useCallback(async () => {
    if (!selectedCompany) return;
    setLoadingMasterEmps(true);
    try {
      const { data } = await axios.get(`${API_URL}/employees/lookup?company_id=${selectedCompany}&limit=1000`, { withCredentials: true });
      const items = Array.isArray(data) ? data : (data.items || []);
      setMasterEmployees(items);
    } catch (e) { console.error(e); }
    setLoadingMasterEmps(false);
  }, [selectedCompany]);

  const fetchTemplates = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/documents/templates?company_id=${selectedCompany}&doc_type=${docType}`, { withCredentials: true });
      setTemplates(data);
      const def = data.find(t => t.is_default) || data[0];
      if (def) setSelectedTemplateId(def.id || def._id);
    } catch (e) { console.error(e); }
  }, [selectedCompany, docType]);

  useEffect(() => {
    if (selectedCompany) {
      fetchTemplates();
      fetchIssuedDocs();
      fetchMasterEmployees();
    }
  }, [selectedCompany, fetchTemplates, fetchIssuedDocs, fetchMasterEmployees]);

  // Unique filter lists for multi-employee selector
  const departments = useMemo(() => {
    const set = new Set();
    masterEmployees.forEach(e => {
      const d = e.department_name || e.department || e.employment_details?.department_name || e.employment_details?.department;
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [masterEmployees]);

  const designations = useMemo(() => {
    const set = new Set();
    masterEmployees.forEach(e => {
      const d = e.designation_name || e.designation || e.employment_details?.designation_name || e.employment_details?.designation;
      if (d) set.add(d);
    });
    return Array.from(set).sort();
  }, [masterEmployees]);

  const branches = useMemo(() => {
    const set = new Set();
    masterEmployees.forEach(e => {
      const b = e.location_name || e.location || e.employment_details?.location_name || e.employment_details?.location || e.branch;
      if (b) set.add(b);
    });
    return Array.from(set).sort();
  }, [masterEmployees]);

  // Client-side filtered employees
  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return masterEmployees.filter(emp => {
      const code = (emp.employee_code || emp.employment_details?.employee_code || '').toLowerCase();
      const first = (emp.first_name || emp.personal_details?.first_name || '').toLowerCase();
      const last = (emp.last_name || emp.personal_details?.last_name || '').toLowerCase();
      const name = (emp.full_name || emp.name || `${first} ${last}`).toLowerCase();
      const dept = (emp.department_name || emp.department || emp.employment_details?.department_name || '').toLowerCase();
      const desig = (emp.designation_name || emp.designation || emp.employment_details?.designation_name || '').toLowerCase();
      const loc = (emp.location_name || emp.location || emp.employment_details?.location_name || '').toLowerCase();
      const status = (emp.employment_status || emp.status || emp.employment_details?.employment_status || 'active').toLowerCase();
      const email = (emp.email || emp.work_email || emp.employment_details?.work_email || '').toLowerCase();

      if (q && !code.includes(q) && !name.includes(q) && !dept.includes(q) && !desig.includes(q) && !email.includes(q)) {
        return false;
      }
      if (deptFilter !== 'all' && (emp.department_name || emp.department || emp.employment_details?.department_name) !== deptFilter) {
        return false;
      }
      if (desigFilter !== 'all' && (emp.designation_name || emp.designation || emp.employment_details?.designation_name) !== desigFilter) {
        return false;
      }
      if (branchFilter !== 'all' && (emp.location_name || emp.location || emp.employment_details?.location_name) !== branchFilter) {
        return false;
      }
      if (statusFilter !== 'all') {
        if (statusFilter === 'active' && !['active', 'probation', 'notice_period'].includes(status)) return false;
        if (statusFilter !== 'active' && status !== statusFilter.toLowerCase()) return false;
      }

      return true;
    });
  }, [masterEmployees, searchQuery, deptFilter, desigFilter, branchFilter, statusFilter]);

  // Load employee salary structure driven by template configuration (single mode)
  const loadEmployeeSalary = useCallback(async (empId, tId) => {
    if (!empId) { setOriginalSections(null); setEditedSections(null); return; }
    setSalaryLoading(true);
    setIsDirty(false);
    try {
      const activeTId = tId !== undefined ? tId : selectedTemplateId;
      const { data } = await axios.get(
        `${API_URL}/documents/salary-preview/${empId}?company_id=${selectedCompany}&template_id=${activeTId || ''}&doc_type=${docType}`,
        { withCredentials: true }
      );
      const sd = data.salary_data || {};
      const sections = (sd.sections || []).map((sec, sIdx) => ({
        ...sec,
        components: (sec.components || []).map((c, cIdx) => ({
          ...c,
          monthly_amount: Number(c.monthly) || Number(c.monthly_amount) || 0,
          annual_amount: Number(c.annual) || Number(c.annual_amount) || 0,
          _enabled: c.enabled !== false,
          _key: `sec_${sIdx}_comp_${cIdx}_${c.code || c.head_code}`,
        }))
      }));
      setOriginalSections(sections);
      setEditedSections(JSON.parse(JSON.stringify(sections)));
    } catch (e) {
      toast.error('Could not load salary data for this employee.');
      setOriginalSections(null);
      setEditedSections(null);
    } finally {
      setSalaryLoading(false);
    }
  }, [selectedCompany, selectedTemplateId, docType]);

  const handleSelectEmployee = (empId, empObj) => {
    setSelectedEmployee(empId);
    setEmployeeDoc(empObj);
    if (SALARY_EDITOR_DOC_TYPES.has(docType)) {
      loadEmployeeSalary(empId, selectedTemplateId);
    }
  };

  const handleDocTypeChange = (newDocType) => {
    setDocType(newDocType);
    setValidationResult(null);
    if (newDocType === 'increment_letter') {
      setIsBulkMode(true);
    }
    if (!SALARY_EDITOR_DOC_TYPES.has(newDocType)) {
      setOriginalSections(null);
      setEditedSections(null);
    } else if (selectedEmployee) {
      loadEmployeeSalary(selectedEmployee, selectedTemplateId);
    }
  };

  const handleTemplateChange = (newTemplateId) => {
    setSelectedTemplateId(newTemplateId);
    setValidationResult(null);
    if (selectedEmployee && SALARY_EDITOR_DOC_TYPES.has(docType)) {
      loadEmployeeSalary(selectedEmployee, newTemplateId);
    }
  };

  // Multi-employee selection handlers
  const handleToggleSelectEmployee = (emp) => {
    const empId = emp.id || emp._id;
    setSelectedEmployees(prev => {
      const isSelected = prev.includes(empId);
      if (isSelected) {
        setSelectedEmployeeDetails(prevDet => prevDet.filter(d => (d.id || d._id) !== empId));
        return prev.filter(i => i !== empId);
      } else {
        setSelectedEmployeeDetails(prevDet => [...prevDet, emp]);
        return [...prev, empId];
      }
    });
    setValidationResult(null);
  };

  const handleSelectAllFiltered = () => {
    const newIds = new Set(selectedEmployees);
    const newDetailsMap = new Map(selectedEmployeeDetails.map(d => [d.id || d._id, d]));

    filteredEmployees.forEach(emp => {
      const empId = emp.id || emp._id;
      newIds.add(empId);
      newDetailsMap.set(empId, emp);
    });

    setSelectedEmployees(Array.from(newIds));
    setSelectedEmployeeDetails(Array.from(newDetailsMap.values()));
    setValidationResult(null);
    toast.success(`Selected ${filteredEmployees.length} filtered employees.`);
  };

  const handleClearSelection = () => {
    setSelectedEmployees([]);
    setSelectedEmployeeDetails([]);
    setValidationResult(null);
    toast.info('Employee selection cleared.');
  };

  const handleRemoveChip = (empId) => {
    setSelectedEmployees(prev => prev.filter(i => i !== empId));
    setSelectedEmployeeDetails(prev => prev.filter(d => (d.id || d._id) !== empId));
    setValidationResult(null);
  };

  // Bulk Validation Handler
  const handleValidateBulk = async () => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee for validation');
      return;
    }
    setValidating(true);
    try {
      const payload = {
        company_id: selectedCompany,
        employee_ids: selectedEmployees,
        doc_type: docType,
        template_id: selectedTemplateId || null,
      };
      const { data } = await axios.post(`${API_URL}/documents/validate-bulk-increments`, payload, { withCredentials: true });
      setValidationResult(data);
      toast.success(`Validation complete: ${data.eligible_count} eligible, ${data.ineligible_count} ineligible, ${data.already_issued_count} already issued.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setValidating(false);
    }
  };

  // Bulk Generation Handler
  const handleGenerateBulk = async (forceReissue = false) => {
    if (selectedEmployees.length === 0) {
      toast.error('Please select at least one employee first');
      return;
    }
    setBulkGenerating(true);
    try {
      const payload = {
        company_id: selectedCompany,
        employee_ids: selectedEmployees,
        doc_type: docType,
        template_id: selectedTemplateId || null,
        force_reissue: forceReissue,
      };
      const { data } = await axios.post(`${API_URL}/documents/generate-bulk`, payload, { withCredentials: true });
      setBulkResultModal(data);
      toast.success(`Bulk generation complete! Succeeded: ${data.succeeded_count}, Skipped: ${data.skipped_duplicate_count}, Failed: ${data.failed_count}`);
      fetchIssuedDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk generation failed');
    } finally {
      setBulkGenerating(false);
    }
  };

  // Bulk Email Dispatch Handler
  const handleBulkEmailDispatch = async (docIds) => {
    if (!docIds || docIds.length === 0) {
      toast.error('No document IDs provided for email dispatch');
      return;
    }
    setBulkEmailing(true);
    try {
      const { data } = await axios.post(`${API_URL}/documents/bulk-email`, { document_ids: docIds }, { withCredentials: true });
      setBulkEmailResultModal(data);
      if (data.sent_count > 0) {
        toast.success(`Dispatched emails to ${data.sent_count} employees.`);
      }
      if (data.failed_count > 0) {
        toast.warning(`${data.failed_count} emails could not be sent. Check details below.`);
      }
      fetchIssuedDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk email dispatch failed');
    } finally {
      setBulkEmailing(false);
    }
  };

  // Live totals from edited sections (single mode)
  const liveTotals = useMemo(() => {
    if (!editedSections) return {};
    return computeLiveTotals(editedSections);
  }, [editedSections]);

  const handleComponentChange = (secIndex, compIndex, updatedComp) => {
    setEditedSections((prevSections) => {
      if (!prevSections) return prevSections;
      return prevSections.map((sec, sIdx) => {
        if (sIdx !== secIndex) return sec;
        const newComps = sec.components.map((c, cIdx) => (cIdx === compIndex ? updatedComp : c));
        return { ...sec, components: newComps };
      });
    });
    setIsDirty(true);
  };

  const handleResetSalary = () => {
    if (!originalSections) return;
    setEditedSections(JSON.parse(JSON.stringify(originalSections)));
    setIsDirty(false);
    toast.success('Salary reset to original template & master values.');
  };

  const buildOverrideComponents = () => {
    if (!editedSections) return [];
    const list = [];
    for (const sec of editedSections) {
      for (const c of (sec.components || [])) {
        if (c._enabled !== false) {
          list.push({
            head_code: c.code || c.head_code,
            head_name: c.name || c.head_name,
            head_type: c.head_type || sec.category_type,
            category_type: sec.category_type,
            monthly_amount: Number(c.monthly_amount) || 0,
            annual_amount: Number(c.annual_amount) || 0,
            enabled: true,
          });
        }
      }
    }
    return list;
  };

  const handleGenerateAndIssue = async (forceUpdateMaster = false) => {
    if (!selectedEmployee) { toast.error('Please select an employee first'); return; }
    setShowMasterUpdateConfirm(false);
    setGenerating(true);
    try {
      const showEditor = SALARY_EDITOR_DOC_TYPES.has(docType) && editedSections;
      const payload = {
        company_id: selectedCompany,
        employee_id: selectedEmployee,
        doc_type: docType,
        template_id: selectedTemplateId || null,
        salary_override_components: showEditor && isDirty ? buildOverrideComponents() : null,
        update_employee_salary_master: forceUpdateMaster,
        salary_edit_reason: salaryEditReason || null,
      };

      await axios.post(`${API_URL}/documents/generate`, payload, { withCredentials: true });

      const label = catalog.find(c => c.type === docType)?.label || 'Document';
      toast.success(`${label} issued successfully!`);
      setSalaryEditReason('');
      setIsDirty(false);
      fetchIssuedDocs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Document issuance failed');
    }
    setGenerating(false);
  };

  const handleSendEmail = async (docId) => {
    try {
      const { data } = await axios.post(`${API_URL}/documents/issued/${docId}/send-email`, {}, { withCredentials: true });
      toast.success(data.message);
      fetchIssuedDocs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to dispatch email'); }
  };

  const handleViewAuditLog = async (docId) => {
    try {
      const { data } = await axios.get(`${API_URL}/documents/issued/${docId}/audit-log`, { withCredentials: true });
      setAuditLog(data);
      setShowAuditModal(true);
    } catch (err) { toast.error('Failed to fetch audit log'); }
  };

  const showSalaryEditor = !isBulkMode && SALARY_EDITOR_DOC_TYPES.has(docType);

  // Issued Documents Repository multi-select handlers
  const toggleSelectIssuedDoc = (docId) => {
    setIssuedSelectedDocs(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const toggleSelectAllIssuedDocs = () => {
    if (issuedSelectedDocs.size === issuedDocs.length) {
      setIssuedSelectedDocs(new Set());
    } else {
      setIssuedSelectedDocs(new Set(issuedDocs.map(d => d.id || d._id)));
    }
  };

  const handleBulkDownloadIssued = async () => {
    const list = Array.from(issuedSelectedDocs);
    if (list.length === 0) return;
    toast.info(`Starting download for ${list.length} documents...`);
    for (const id of list) {
      const doc = issuedDocs.find(d => (d.id || d._id) === id);
      const code = doc?.employee_code || 'EMP';
      const dtype = doc?.doc_type || 'document';
      await downloadDocumentPdf(id, `${code}_${dtype}.pdf`);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-5 p-6 max-w-7xl mx-auto" data-testid="appointment-orders-page">

        {/* ── Page Header ── */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <FileCheck className="w-7 h-7 text-[#0052FF]" /> Official Letter &amp; Document Workbench
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Generate dynamic single &amp; bulk Official Letters driven by template structure with automated increment integration.
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Mode Toggle Button */}
            <div className="bg-slate-100 p-1 rounded-xl flex items-center gap-1 border border-slate-200">
              <button
                onClick={() => setIsBulkMode(false)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${!isBulkMode ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500 hover:text-slate-900'}`}
              >
                Single Employee
              </button>
              <button
                onClick={() => setIsBulkMode(true)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${isBulkMode ? 'bg-[#0052FF] text-white shadow-xs' : 'text-slate-500 hover:text-slate-900'}`}
              >
                Bulk Generation
              </button>
            </div>

            <select
              value={selectedCompany}
              onChange={e => setSelectedCompany(e.target.value)}
              className="border border-slate-300 rounded-xl px-3 py-2 text-sm bg-white font-medium shadow-xs"
              data-testid="appt-company-select"
            >
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        {/* ── Step 1-3: Document Generation Controls ── */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" /> Document Configuration
            </h3>
            {docType === 'increment_letter' && (
              <span className="bg-blue-50 text-blue-800 border border-blue-200 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1">
                <Users className="w-3.5 h-3.5" /> Multi-Employee Bulk Mode Active
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

            {/* Document Type Select */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white font-extrabold text-[9px] mr-1">1</span>
                Document Type *
              </label>
              <select
                value={docType}
                onChange={e => handleDocTypeChange(e.target.value)}
                className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium outline-none focus:bg-white focus:border-blue-500"
                data-testid="doctype-select"
              >
                {catalog.map(c => <option key={c.type} value={c.type}>{c.label}</option>)}
              </select>
            </div>

            {/* Template Version Select */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white font-extrabold text-[9px] mr-1">2</span>
                Template Version
              </label>
              <select
                value={selectedTemplateId}
                onChange={e => handleTemplateChange(e.target.value)}
                className="w-full h-10 px-3 bg-slate-50 border border-slate-300 rounded-xl text-sm font-medium outline-none focus:bg-white focus:border-blue-500"
                data-testid="template-version-select"
              >
                {templates.map(t => (
                  <option key={t.id || t._id} value={t.id || t._id}>
                    {t.template_name} (v{t.version || 1}) {t.is_default ? '★ Default' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Single Employee Picker (Visible in Single Mode) */}
            {!isBulkMode && (
              <div>
                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-blue-600 text-white font-extrabold text-[9px] mr-1">3</span>
                  Select Employee *
                </label>
                <EmployeeSelect
                  companyId={selectedCompany}
                  value={selectedEmployee}
                  onChange={handleSelectEmployee}
                  placeholder="Type name or code (e.g. EMP001)..."
                  data-testid="appt-employee-select"
                />
                {salaryLoading && (
                  <div className="flex items-center gap-1.5 mt-1.5 text-xs text-blue-600">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading template salary structure...
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── MULTI-EMPLOYEE SELECTION PANEL (for Increment Letters & Bulk Mode) ── */}
        {isBulkMode && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" /> Multi-Employee Selection &amp; Filters
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Filter employee master by department, designation, branch, or search. Select individual employees or select all filtered.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-extrabold bg-blue-100 text-blue-900 px-3 py-1 rounded-full border border-blue-200">
                  {selectedEmployees.length} Employees Selected
                </span>
                <button
                  onClick={handleSelectAllFiltered}
                  className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                >
                  <CheckSquare className="w-3.5 h-3.5" /> Select All Filtered ({filteredEmployees.length})
                </button>
                <button
                  onClick={handleClearSelection}
                  disabled={selectedEmployees.length === 0}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded-xl text-xs font-bold transition-colors flex items-center gap-1"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Clear Selection
                </button>
              </div>
            </div>

            {/* ── Filters Bar ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3 bg-slate-50 p-3.5 rounded-xl border border-slate-200">
              <div className="relative md:col-span-1">
                <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-3" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search code, name..."
                  className="w-full h-9 pl-8 pr-3 bg-white border border-slate-300 rounded-lg text-xs outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <select
                  value={deptFilter}
                  onChange={e => setDeptFilter(e.target.value)}
                  className="w-full h-9 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium outline-none"
                >
                  <option value="all">All Departments ({departments.length})</option>
                  {departments.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div>
                <select
                  value={desigFilter}
                  onChange={e => setDesigFilter(e.target.value)}
                  className="w-full h-9 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium outline-none"
                >
                  <option value="all">All Designations ({designations.length})</option>
                  {designations.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>

              <div>
                <select
                  value={branchFilter}
                  onChange={e => setBranchFilter(e.target.value)}
                  className="w-full h-9 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium outline-none"
                >
                  <option value="all">All Branches ({branches.length})</option>
                  {branches.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div>
                <select
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="w-full h-9 px-2.5 bg-white border border-slate-300 rounded-lg text-xs font-medium outline-none"
                >
                  <option value="all">All Employment Statuses</option>
                  <option value="active">Active &amp; Probation</option>
                  <option value="notice_period">Notice Period</option>
                </select>
              </div>
            </div>

            {/* ── Selected Employee Removable Chips Bar ── */}
            {selectedEmployees.length > 0 && (
              <div className="bg-blue-50/60 border border-blue-200 p-3 rounded-xl space-y-2">
                <div className="flex items-center justify-between text-xs font-bold text-blue-900">
                  <span>Selected Employees ({selectedEmployees.length}) — Click chip to remove:</span>
                  <button onClick={handleClearSelection} className="text-[11px] text-rose-600 hover:underline">Remove All</button>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto p-1">
                  {selectedEmployeeDetails.map(emp => {
                    const empId = emp.id || emp._id;
                    const code = emp.employee_code || emp.employment_details?.employee_code || empId.slice(0, 6);
                    const name = emp.full_name || emp.name || `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.strip() || 'Employee';
                    return (
                      <span
                        key={empId}
                        className="inline-flex items-center gap-1.5 bg-white text-slate-800 border border-blue-300 text-xs font-medium rounded-full px-3 py-1 shadow-2xs"
                      >
                        <span className="font-mono text-[10px] text-blue-700 font-bold">{code}</span>
                        <span>{name}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveChip(empId)}
                          className="hover:bg-rose-100 rounded-full p-0.5 text-rose-600 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Filtered Employee Master Table ── */}
            <div className="border border-slate-200 rounded-xl overflow-hidden max-h-72 overflow-y-auto">
              {loadingMasterEmps ? (
                <div className="p-8 text-center text-xs text-slate-500">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2 text-blue-600" /> Loading employee master...
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 italic">
                  No employees match the current filter criteria.
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-700 font-bold uppercase sticky top-0 z-10 border-b border-slate-200">
                    <tr>
                      <th className="p-2.5 w-10 text-center">
                        <input
                          type="checkbox"
                          checked={filteredEmployees.length > 0 && filteredEmployees.every(e => selectedEmployees.includes(e.id || e._id))}
                          onChange={e => {
                            if (e.target.checked) handleSelectAllFiltered();
                            else {
                              const filteredSet = new Set(filteredEmployees.map(e => e.id || e._id));
                              setSelectedEmployees(prev => prev.filter(id => !filteredSet.has(id)));
                              setSelectedEmployeeDetails(prev => prev.filter(d => !filteredSet.has(d.id || d._id)));
                            }
                          }}
                          className="accent-blue-600 cursor-pointer"
                        />
                      </th>
                      <th className="p-2.5">Code</th>
                      <th className="p-2.5">Employee Name</th>
                      <th className="p-2.5">Department</th>
                      <th className="p-2.5">Designation</th>
                      <th className="p-2.5">Branch</th>
                      <th className="p-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-slate-800">
                    {filteredEmployees.map(emp => {
                      const empId = emp.id || emp._id;
                      const isSelected = selectedEmployees.includes(empId);
                      const code = emp.employee_code || emp.employment_details?.employee_code || empId.slice(0, 6);
                      const name = emp.full_name || emp.name || `${emp.personal_details?.first_name || ''} ${emp.personal_details?.last_name || ''}`.strip() || 'Employee';
                      const dept = emp.department_name || emp.department || emp.employment_details?.department_name || 'General';
                      const desig = emp.designation_name || emp.designation || emp.employment_details?.designation_name || 'Staff';
                      const loc = emp.location_name || emp.location || emp.employment_details?.location_name || 'Head Office';
                      const status = emp.employment_status || emp.status || emp.employment_details?.employment_status || 'active';

                      return (
                        <tr
                          key={empId}
                          onClick={() => handleToggleSelectEmployee(emp)}
                          className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/80 font-medium' : 'hover:bg-slate-50'}`}
                        >
                          <td className="p-2.5 text-center" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectEmployee(emp)}
                              className="accent-blue-600 cursor-pointer"
                            />
                          </td>
                          <td className="p-2.5 font-mono text-[11px] font-bold text-blue-700">{code}</td>
                          <td className="p-2.5 font-bold text-slate-900">{name}</td>
                          <td className="p-2.5 text-slate-600">{dept}</td>
                          <td className="p-2.5 text-slate-600">{desig}</td>
                          <td className="p-2.5 text-slate-500">{loc}</td>
                          <td className="p-2.5 capitalize font-semibold">
                            <span className="bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5 rounded-full text-[10px]">
                              {status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Validation & Batch Execution Control Bar ── */}
            <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-slate-200">
              <div className="text-xs text-slate-500">
                Ready to process <b>{selectedEmployees.length}</b> selected employees for Increment Letters.
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleValidateBulk}
                  disabled={validating || selectedEmployees.length === 0}
                  className="bg-slate-800 hover:bg-slate-900 text-white px-4 h-10 rounded-xl text-xs font-semibold shadow-xs"
                >
                  {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-amber-400" />}
                  Validate Increment Records ({selectedEmployees.length})
                </Button>

                <Button
                  onClick={() => handleGenerateBulk(false)}
                  disabled={bulkGenerating || selectedEmployees.length === 0}
                  className="bg-[#0052FF] hover:bg-[#0040CC] text-white px-5 h-10 rounded-xl text-xs font-bold shadow-md shadow-blue-200"
                >
                  {bulkGenerating ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <FileText className="w-4 h-4 mr-1.5" />}
                  Generate &amp; Issue Letters ({selectedEmployees.length})
                </Button>
              </div>
            </div>

            {/* ── VALIDATION SUMMARY PANEL (Displayed after clicking Validate) ── */}
            {validationResult && (
              <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-bold text-sm text-slate-900 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Increment Letter Validation Summary
                  </h4>
                  <div className="flex gap-2 text-xs font-bold">
                    <span className="bg-emerald-100 text-emerald-800 px-2.5 py-0.5 rounded-full">
                      {validationResult.eligible_count} Eligible
                    </span>
                    <span className="bg-rose-100 text-rose-800 px-2.5 py-0.5 rounded-full">
                      {validationResult.ineligible_count} Ineligible
                    </span>
                    <span className="bg-amber-100 text-amber-800 px-2.5 py-0.5 rounded-full">
                      {validationResult.already_issued_count} Already Issued
                    </span>
                  </div>
                </div>

                {/* Eligible Employees Breakdown */}
                {validationResult.eligible_count > 0 && (
                  <div className="bg-white border border-emerald-200 rounded-xl p-3.5 space-y-2">
                    <div className="text-xs font-bold text-emerald-900 flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4 text-emerald-600" />
                      Eligible Employees with Approved Increment Data ({validationResult.eligible_count})
                    </div>
                    <div className="overflow-x-auto border border-emerald-100 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-emerald-50 text-emerald-900 font-bold uppercase text-[10px]">
                          <tr>
                            <th className="p-2 text-left">Code</th>
                            <th className="p-2 text-left">Employee Name</th>
                            <th className="p-2 text-right">Previous CTC</th>
                            <th className="p-2 text-right">Revised CTC</th>
                            <th className="p-2 text-right">Increment</th>
                            <th className="p-2 text-center">Effective Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-medium">
                          {validationResult.eligible.map(e => (
                            <tr key={e.employee_id} className="hover:bg-slate-50">
                              <td className="p-2 font-mono font-bold text-blue-700">{e.employee_code}</td>
                              <td className="p-2 font-bold text-slate-900">{e.employee_name}</td>
                              <td className="p-2 text-right font-mono text-slate-600">{INR(e.increment_info?.previous_ctc)}</td>
                              <td className="p-2 text-right font-mono font-bold text-slate-900">{INR(e.increment_info?.new_ctc)}</td>
                              <td className="p-2 text-right font-mono font-extrabold text-emerald-700">
                                +{INR(e.increment_info?.increment_amount)} ({e.increment_info?.percentage}%)
                              </td>
                              <td className="p-2 text-center font-mono text-slate-600">{e.increment_info?.effective_from}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Ineligible Employees Breakdown */}
                {validationResult.ineligible_count > 0 && (
                  <div className="bg-rose-50/70 border border-rose-200 rounded-xl p-3.5 space-y-2">
                    <div className="text-xs font-bold text-rose-900 flex items-center gap-1.5">
                      <AlertCircle className="w-4 h-4 text-rose-600" />
                      Ineligible / Missing Approved Increment Data ({validationResult.ineligible_count})
                    </div>
                    <div className="overflow-x-auto border border-rose-200 bg-white rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-rose-100 text-rose-900 font-bold uppercase text-[10px]">
                          <tr>
                            <th className="p-2 text-left">Code</th>
                            <th className="p-2 text-left">Employee Name</th>
                            <th className="p-2 text-left">Reason / Validation Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 font-medium">
                          {validationResult.ineligible.map(e => (
                            <tr key={e.employee_id}>
                              <td className="p-2 font-mono font-bold text-rose-700">{e.employee_code}</td>
                              <td className="p-2 font-bold text-slate-900">{e.employee_name}</td>
                              <td className="p-2 text-rose-700">{e.reason}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Step 4: Single Employee Salary Breakup Editor ── */}
        {showSalaryEditor && (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-3.5 border-b border-slate-200 bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-400 text-slate-900 font-extrabold text-[10px]">4</span>
                <Edit3 className="w-4 h-4 text-amber-400" />
                <span className="font-bold text-sm">Template Salary Breakup Editor</span>
                {isDirty && (
                  <span className="text-[10px] font-bold bg-amber-400 text-slate-900 px-2 py-0.5 rounded-full ml-1">EDITED</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowAnnexurePreview(p => !p)}
                  className="flex items-center gap-1 text-xs font-bold text-slate-300 hover:text-white px-2.5 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                >
                  {showAnnexurePreview ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                  {showAnnexurePreview ? 'Hide' : 'Show'} Preview
                </button>
                <button
                  onClick={handleResetSalary}
                  className="flex items-center gap-1 text-xs font-bold text-slate-300 hover:text-white px-2.5 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 transition-colors"
                >
                  <RotateCcw className="w-3 h-3" /> Reset to Original
                </button>
              </div>
            </div>

            {!selectedEmployee ? (
              <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <IndianRupee className="w-10 h-10 text-slate-200 stroke-[1.5]" />
                <span>Select an employee above to load the template salary breakup editor.</span>
              </div>
            ) : salaryLoading ? (
              <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                <span>Loading template salary structure &amp; employee values...</span>
              </div>
            ) : !editedSections || editedSections.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm flex flex-col items-center gap-2">
                <AlertTriangle className="w-8 h-8 text-amber-300" />
                <span>No template salary structure found. Default layout will be used.</span>
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Info notice */}
                <div className="flex items-center gap-2.5 bg-blue-50 border border-blue-200 rounded-xl px-4 py-2.5 text-xs text-blue-900">
                  <Info className="w-4 h-4 text-blue-500 shrink-0" />
                  <span>
                    Displaying <b>all sections and salary heads configured in the template</b>.
                    Edits are <b>document-only by default</b>. All changes instantly update Gross, Deductions, Employer Cost, Perks, Net Salary, Fixed CTC, and the Annexure Preview.
                  </span>
                </div>

                {/* CTC Summary bar */}
                <CTCSummaryBar totals={liveTotals} />

                {/* Editor + Live Preview side by side */}
                <div className={`grid gap-5 ${showAnnexurePreview ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
                  {/* Editor: renders all sections in template order */}
                  <div>
                    {editedSections.map((sec, sIdx) => (
                      <SalarySection
                        key={sec.id || sIdx}
                        section={sec}
                        secIndex={sIdx}
                        onChangeComp={handleComponentChange}
                      />
                    ))}
                  </div>

                  {/* Live Preview: renders all sections in template order */}
                  {showAnnexurePreview && (
                    <AnnexurePreview
                      sections={editedSections}
                      totals={liveTotals}
                      employeeName={employeeDoc?.employee_name || employeeDoc?.name}
                    />
                  )}
                </div>

                {/* Reason field */}
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Reason for Salary Edit <span className="text-slate-400 font-normal">(optional — logged in audit trail)</span>
                  </label>
                  <input
                    type="text"
                    value={salaryEditReason}
                    onChange={e => setSalaryEditReason(e.target.value)}
                    placeholder="e.g. Revised CTC as per offer negotiation, Performance-based revision..."
                    className="w-full h-9 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-blue-400 focus:bg-white"
                  />
                </div>

                {/* ── Action Bar ── */}
                <div className="border-t border-slate-200 pt-5">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Generate Options</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">

                    {/* Option 1: Edited values only */}
                    <button
                      onClick={() => handleGenerateAndIssue(false)}
                      disabled={generating || !selectedEmployee}
                      className="group flex flex-col items-start gap-1 bg-[#0052FF] hover:bg-[#0040CC] disabled:opacity-60 text-white rounded-xl p-4 transition-all shadow-md shadow-blue-200 text-left"
                      data-testid="generate-issue-btn"
                    >
                      <div className="flex items-center gap-2 font-bold text-sm">
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                        Generate &amp; Issue Document
                      </div>
                      <div className="text-[10px] text-blue-200 leading-relaxed">
                        Uses edited salary values in the PDF.<br />
                        <b>Employee Master salary is NOT changed.</b>
                      </div>
                      <div className="mt-1.5 text-[10px] bg-white/20 rounded-lg px-2 py-0.5 font-bold">★ Recommended</div>
                    </button>

                    {/* Option 2: Update master too */}
                    <button
                      onClick={() => setShowMasterUpdateConfirm(true)}
                      disabled={generating || !selectedEmployee || !isDirty}
                      className="group flex flex-col items-start gap-1 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded-xl p-4 transition-all shadow-md shadow-amber-200 text-left"
                    >
                      <div className="flex items-center gap-2 font-bold text-sm">
                        <Database className="w-4 h-4" /> Generate &amp; Update Salary Master
                      </div>
                      <div className="text-[10px] text-amber-100 leading-relaxed">
                        Also updates Employee Salary Master.<br />
                        <b>Future payroll will use edited values.</b>
                      </div>
                      {!isDirty && <div className="text-[10px] text-amber-200 mt-1">Edit salary first to enable</div>}
                    </button>

                    {/* Option 3: Reset */}
                    <button
                      onClick={handleResetSalary}
                      disabled={!isDirty}
                      className="flex flex-col items-start gap-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-40 text-slate-700 rounded-xl p-4 transition-all border border-slate-200 text-left"
                    >
                      <div className="flex items-center gap-2 font-bold text-sm">
                        <RotateCcw className="w-4 h-4" /> Cancel Changes
                      </div>
                      <div className="text-[10px] text-slate-500 leading-relaxed">
                        Reload original template salary breakup.<br />
                        Discards all edits.
                      </div>
                    </button>

                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Generate button for non-salary-editor doc types in Single Mode ── */}
        {!isBulkMode && !showSalaryEditor && (
          <div className="flex justify-end">
            <Button
              onClick={() => handleGenerateAndIssue(false)}
              disabled={generating || !selectedEmployee}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white px-6 h-11 rounded-xl shadow-md font-semibold"
              data-testid="generate-issue-btn"
            >
              {generating
                ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" />Generating...</>
                : <><FileText className="w-4 h-4 mr-2" />Generate &amp; Issue Document</>
              }
            </Button>
          </div>
        )}

        {/* ── Issued Documents Repository ── */}
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" /> Issued Documents Repository &amp; Tracking
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Select documents using checkboxes to perform bulk email dispatch or bulk PDF downloads.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {issuedSelectedDocs.size > 0 && (
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold bg-indigo-50 text-indigo-800 px-2.5 py-1 rounded-lg border border-indigo-200">
                    {issuedSelectedDocs.size} Selected
                  </span>
                  <button
                    onClick={() => handleBulkEmailDispatch(Array.from(issuedSelectedDocs))}
                    disabled={bulkEmailing}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-xs flex items-center gap-1.5 shadow-xs"
                  >
                    {bulkEmailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Bulk Email ({issuedSelectedDocs.size})
                  </button>
                  <button
                    onClick={handleBulkDownloadIssued}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl font-semibold text-xs flex items-center gap-1.5 shadow-xs"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Bulk Download PDFs
                  </button>
                </div>
              )}
              <button onClick={fetchIssuedDocs} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 font-medium bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200">
                <RefreshCw className="w-3.5 h-3.5" /> Refresh List
              </button>
            </div>
          </div>

          {loadingIssued ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2 text-indigo-600" /> Loading repository...
            </div>
          ) : issuedDocs.length === 0 ? (
            <div className="py-10 text-center text-slate-400 text-xs italic bg-slate-50 border border-slate-100 rounded-xl">
              No issued documents found. Select employee(s) above and generate documents.
            </div>
          ) : (
            <div className="overflow-x-auto border border-slate-200 rounded-xl">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-700 font-bold uppercase">
                  <tr>
                    <th className="p-3 w-10 text-center">
                      <input
                        type="checkbox"
                        checked={issuedSelectedDocs.size === issuedDocs.length && issuedDocs.length > 0}
                        onChange={toggleSelectAllIssuedDocs}
                        className="accent-indigo-600 cursor-pointer"
                      />
                    </th>
                    <th className="p-3">Employee</th>
                    <th className="p-3">Document Type</th>
                    <th className="p-3">Template &amp; Version</th>
                    <th className="p-3">Salary</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Issued Date</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                  {issuedDocs.map(doc => {
                    const docId = doc.id || doc._id;
                    const isAck = doc.status === 'acknowledged';
                    const hasSalaryOverride = doc.salary_was_overridden;
                    const isChecked = issuedSelectedDocs.has(docId);

                    return (
                      <tr key={docId} className={`transition-colors ${isChecked ? 'bg-indigo-50/60' : 'hover:bg-slate-50/80'}`}>
                        <td className="p-3 text-center">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelectIssuedDoc(docId)}
                            className="accent-indigo-600 cursor-pointer"
                          />
                        </td>
                        <td className="p-3">
                          <div className="font-bold text-slate-900">{doc.employee_name}</div>
                          <div className="font-mono text-[10px] text-slate-500">{doc.employee_code}</div>
                        </td>
                        <td className="p-3 capitalize font-semibold text-slate-900">
                          {(doc.doc_type || '').replace(/_/g, ' ')}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-1.5">
                            <span>{doc.template_name || 'Standard Template'}</span>
                            <span className="font-bold bg-blue-50 text-blue-800 text-[10px] px-1.5 py-0.5 rounded border border-blue-200">
                              v{doc.template_version || 1}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          {hasSalaryOverride ? (
                            <button
                              onClick={() => setSnapshotDoc(doc)}
                              className="flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full hover:bg-amber-200 transition-colors"
                            >
                              <Edit3 className="w-2.5 h-2.5" /> HR-Edited
                            </button>
                          ) : (
                            <button
                              onClick={() => setSnapshotDoc(doc)}
                              className="flex items-center gap-1 text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full hover:bg-slate-200 transition-colors"
                            >
                              <Eye className="w-2.5 h-2.5" /> Master Salary
                            </button>
                          )}
                        </td>
                        <td className="p-3">
                          {isAck ? (
                            <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-emerald-200">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Acknowledged &amp; Signed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 text-[11px] font-bold px-2.5 py-0.5 rounded-full border border-amber-200">
                              <Clock className="w-3.5 h-3.5 text-amber-600" /> Issued (Pending Sign)
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-slate-600 font-mono">
                          {doc.issued_at ? doc.issued_at.slice(0, 10) : '—'}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => downloadDocumentPdf(docId, `${doc.employee_code}_${doc.doc_type}.pdf`)}
                              className="px-2.5 py-1 bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-lg font-semibold text-[11px] flex items-center gap-1 transition-colors shadow-2xs"
                            >
                              <Download className="w-3 h-3" /> Download PDF
                            </button>
                            <button
                              onClick={() => handleSendEmail(docId)}
                              className="px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-[11px] flex items-center gap-1 transition-colors"
                            >
                              <Send className="w-3 h-3" /> Email
                            </button>
                            <button
                              onClick={() => handleViewAuditLog(docId)}
                              className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-semibold text-[11px] flex items-center gap-1 transition-colors"
                            >
                              <ShieldCheck className="w-3 h-3" /> Audit Log
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── BULK GENERATION RESULTS MODAL ── */}
      {bulkResultModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[85vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-700 to-indigo-700 text-white">
              <div>
                <h3 className="font-bold text-base flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-300" />
                  Bulk Generation &amp; Issuance Outcome
                </h3>
                <p className="text-xs text-blue-200 mt-0.5 font-mono">Batch Reference: {bulkResultModal.batch_id}</p>
              </div>
              <button onClick={() => setBulkResultModal(null)} className="text-white/80 hover:text-white text-xl font-bold p-1">&times;</button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              {/* Counter Badges */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] font-bold text-emerald-700 uppercase">Succeeded &amp; Issued</div>
                  <div className="text-2xl font-extrabold text-emerald-900 mt-1">{bulkResultModal.succeeded_count}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] font-bold text-amber-700 uppercase">Skipped (Duplicate)</div>
                  <div className="text-2xl font-extrabold text-amber-900 mt-1">{bulkResultModal.skipped_duplicate_count}</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] font-bold text-rose-700 uppercase">Failed / Validation Error</div>
                  <div className="text-2xl font-extrabold text-rose-900 mt-1">{bulkResultModal.failed_count}</div>
                </div>
              </div>

              {/* Succeeded List */}
              {bulkResultModal.succeeded_count > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-emerald-800 flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Successfully Generated Documents ({bulkResultModal.succeeded_count})
                  </div>
                  <div className="border border-emerald-200 rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-emerald-50 text-emerald-900 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Employee Name</th>
                          <th className="p-2 text-left">Email Address</th>
                          <th className="p-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkResultModal.succeeded.map(s => (
                          <tr key={s.id} className="hover:bg-slate-50">
                            <td className="p-2 font-mono font-bold text-blue-700">{s.employee_code}</td>
                            <td className="p-2 font-bold text-slate-900">{s.employee_name}</td>
                            <td className="p-2 text-slate-600">{s.email || '—'}</td>
                            <td className="p-2 text-right">
                              <button
                                onClick={() => downloadDocumentPdf(s.id, `${s.employee_code}_Increment_Letter.pdf`)}
                                className="px-2 py-0.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded text-[10px] font-bold"
                              >
                                Download
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Skipped Duplicates List */}
              {bulkResultModal.skipped_duplicate_count > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-amber-600" />
                    Skipped Duplicates (Already Issued for Template Version)
                  </div>
                  <div className="border border-amber-200 rounded-xl overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-amber-50 text-amber-900 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Employee Name</th>
                          <th className="p-2 text-left">Issued Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkResultModal.skipped_duplicate.map(d => (
                          <tr key={d.employee_id}>
                            <td className="p-2 font-mono font-bold text-amber-800">{d.employee_code}</td>
                            <td className="p-2 font-bold text-slate-900">{d.employee_name}</td>
                            <td className="p-2 text-slate-600 font-mono">{d.issued_at?.slice(0, 10)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed List */}
              {bulkResultModal.failed_count > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-rose-800 flex items-center gap-1.5">
                    <AlertCircle className="w-4 h-4 text-rose-600" />
                    Failed Documents ({bulkResultModal.failed_count})
                  </div>
                  <div className="border border-rose-200 rounded-xl overflow-hidden bg-rose-50/50">
                    <table className="w-full text-xs">
                      <thead className="bg-rose-100 text-rose-900 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Employee Name</th>
                          <th className="p-2 text-left">Error Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkResultModal.failed.map(f => (
                          <tr key={f.employee_id}>
                            <td className="p-2 font-mono font-bold text-rose-800">{f.employee_code}</td>
                            <td className="p-2 font-bold text-slate-900">{f.employee_name}</td>
                            <td className="p-2 text-rose-700">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
              <button
                onClick={() => setBulkResultModal(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-xs font-bold"
              >
                Close
              </button>

              {bulkResultModal.succeeded_count > 0 && (
                <button
                  onClick={() => handleBulkEmailDispatch(bulkResultModal.succeeded.map(s => s.id))}
                  disabled={bulkEmailing}
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-md flex items-center gap-1.5"
                >
                  {bulkEmailing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Dispatch Bulk Emails to Issued Letters ({bulkResultModal.succeeded_count})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── BULK EMAIL DISPATCH RESULT MODAL ── */}
      {bulkEmailResultModal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden flex flex-col max-h-[80vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-slate-800 to-slate-900 text-white">
              <h3 className="font-bold text-base flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-400" />
                Bulk Email Delivery Status Summary
              </h3>
              <button onClick={() => setBulkEmailResultModal(null)} className="text-slate-400 hover:text-white text-xl font-bold p-1">&times;</button>
            </div>

            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] font-bold text-emerald-700 uppercase">Emails Sent Successfully</div>
                  <div className="text-2xl font-extrabold text-emerald-900 mt-1">{bulkEmailResultModal.sent_count}</div>
                </div>
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-center">
                  <div className="text-[10px] font-bold text-rose-700 uppercase">Failed / Missing Email Address</div>
                  <div className="text-2xl font-extrabold text-rose-900 mt-1">{bulkEmailResultModal.failed_count}</div>
                </div>
              </div>

              {/* Sent Deliveries List */}
              {bulkEmailResultModal.sent_count > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-emerald-800 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Successfully Delivered Emails ({bulkEmailResultModal.sent_count})
                  </div>
                  <div className="border border-emerald-200 rounded-xl overflow-hidden text-xs">
                    <table className="w-full">
                      <thead className="bg-emerald-50 text-emerald-900 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Employee Name</th>
                          <th className="p-2 text-left">Email Address</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkEmailResultModal.sent.map(s => (
                          <tr key={s.doc_id}>
                            <td className="p-2 font-mono font-bold text-blue-700">{s.employee_code}</td>
                            <td className="p-2 font-bold text-slate-900">{s.employee_name}</td>
                            <td className="p-2 font-mono text-slate-600">{s.email}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Failed Deliveries List */}
              {bulkEmailResultModal.failed_count > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-bold text-rose-800 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4 text-rose-600" /> Delivery Failures ({bulkEmailResultModal.failed_count})
                  </div>
                  <div className="border border-rose-200 rounded-xl overflow-hidden bg-rose-50/50 text-xs">
                    <table className="w-full">
                      <thead className="bg-rose-100 text-rose-900 font-bold uppercase text-[10px]">
                        <tr>
                          <th className="p-2 text-left">Code</th>
                          <th className="p-2 text-left">Employee Name</th>
                          <th className="p-2 text-left">Error Reason</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {bulkEmailResultModal.failed.map(f => (
                          <tr key={f.doc_id}>
                            <td className="p-2 font-mono font-bold text-rose-800">{f.employee_code || 'EMP'}</td>
                            <td className="p-2 font-bold text-slate-900">{f.employee_name || 'Employee'}</td>
                            <td className="p-2 text-rose-700">{f.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
              <button
                onClick={() => setBulkEmailResultModal(null)}
                className="px-4 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-xl text-xs font-bold"
              >
                Close
              </button>
              {bulkEmailResultModal.failed_count > 0 && (
                <button
                  onClick={() => handleBulkEmailDispatch(bulkEmailResultModal.failed.map(f => f.doc_id))}
                  disabled={bulkEmailing}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs font-bold flex items-center gap-1.5"
                >
                  {bulkEmailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Retry Failed Emails ({bulkEmailResultModal.failed_count})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── MASTER UPDATE CONFIRMATION MODAL ── */}
      {showMasterUpdateConfirm && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center gap-3 text-amber-600">
              <AlertTriangle className="w-7 h-7 shrink-0" />
              <h3 className="font-bold text-slate-900 text-lg">Update Employee Salary Master?</h3>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-900 space-y-1.5">
              <p className="font-bold">This will permanently update the employee's salary structure in the system.</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-800">
                <li>Future payroll runs will use the new salary values</li>
                <li>CTC register will be updated</li>
                <li>Salary history will record this change</li>
              </ul>
              <p className="font-bold mt-2">The generated document will also use the edited values.</p>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Reason for Update <span className="text-rose-500">*</span></label>
              <input
                type="text"
                value={salaryEditReason}
                onChange={e => setSalaryEditReason(e.target.value)}
                placeholder="Required — e.g. CTC revision as per appraisal cycle..."
                className="w-full h-9 px-3 bg-slate-50 border border-slate-300 rounded-xl text-sm outline-none focus:border-amber-400 focus:bg-white"
                required
              />
            </div>
            <div className="flex justify-end gap-2 pt-1 border-t border-slate-200 text-xs font-bold">
              <button onClick={() => setShowMasterUpdateConfirm(false)} className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl flex items-center gap-1.5">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
              <button
                onClick={() => {
                  if (!salaryEditReason.trim()) { toast.error('Please enter a reason for updating the master salary.'); return; }
                  handleGenerateAndIssue(true);
                }}
                disabled={generating}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 rounded-xl shadow-md flex items-center gap-1.5 disabled:opacity-60"
              >
                <Database className="w-3.5 h-3.5" /> {generating ? 'Generating...' : 'Confirm — Generate & Update Master'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AUDIT TRAIL MODAL ── */}
      {showAuditModal && auditLog && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
              <h3 className="font-bold text-slate-900 text-base flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-indigo-600" /> Audit Trail — {auditLog.employee_name} ({auditLog.employee_code})
              </h3>
              <button onClick={() => setShowAuditModal(false)} className="text-slate-400 hover:text-slate-600 p-1 text-xl font-bold">✕</button>
            </div>
            <div className="p-6 space-y-3 max-h-[70vh] overflow-y-auto text-xs">
              {(auditLog.audit_trail || []).map((log, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1.5">
                  <div className="flex items-center justify-between text-slate-500 font-mono text-[11px]">
                    <span>{log.timestamp ? new Date(log.timestamp).toLocaleString() : '—'}</span>
                    <span className="font-bold uppercase text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-200">{log.action}</span>
                  </div>
                  <p className="font-medium text-slate-800">{log.details}</p>
                  {log.salary_overridden && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-amber-800 text-[11px] flex items-center gap-1">
                      <Edit3 className="w-3 h-3" />
                      Salary edited before issuance.
                      {log.master_salary_updated ? ' Master updated.' : ' Master NOT changed.'}
                      {log.salary_edit_reason && ` Reason: ${log.salary_edit_reason}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SALARY SNAPSHOT MODAL ── */}
      {snapshotDoc && <SalarySnapshotModal doc={snapshotDoc} onClose={() => setSnapshotDoc(null)} />}
    </MainLayout>
  );
}

